import { useEffect, useMemo, useState } from 'react';
import {
    isProductionCalculationReady,
    PRODUCTION_CALCULATION_TYPES,
    SHADE_CALCULATIONS,
    siteCalculationState,
} from '../services/siteCalculationState';
import { CalculationType } from '../@types/enums';
import { useViewerStore } from '../store/store';
import { logger } from '../services/loggingService';
import {
    beginFinalizeModuleGraph,
    endFinalizeModuleGraph,
} from '../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/TreeImpactEligibility';

export const EXPORT_CALC_WAIT_MS = 120_000;

export async function waitWhileCalculations(
    predicate: () => boolean,
    maxMs = EXPORT_CALC_WAIT_MS,
    timeoutLogMessage = 'Calculation prep timed out waiting for calculations',
): Promise<void> {
    const started = Date.now();
    while (predicate()) {
        if (Date.now() - started > maxMs) {
            logger.warn(timeoutLogMessage);
            return;
        }
        await new Promise((resolve) => setTimeout(resolve, 100));
    }
}

/** Runs shade recalc and waits for the production calculation chain before export. */
export async function runShadeAndProductionCalculationPrep(): Promise<void> {
    beginFinalizeModuleGraph();
    try {
        siteCalculationState.needsUpdate(SHADE_CALCULATIONS);
        await siteCalculationState.useCalculations(SHADE_CALCULATIONS);
        await waitWhileCalculations(() => useViewerStore.getState().refreshingShade);
        await waitWhileCalculations(
            () =>
                PRODUCTION_CALCULATION_TYPES.some((type) =>
                    siteCalculationState.doesNeedUpdateOrUpdating(type),
                ) || siteCalculationState.doesNeedUpdateOrUpdating(SHADE_CALCULATIONS),
        );
    } finally {
        endFinalizeModuleGraph();
    }
}

function isAnyProductionCalculationPending(): boolean {
    return PRODUCTION_CALCULATION_TYPES.some((name) =>
        siteCalculationState.doesNeedUpdateOrUpdating(name),
    );
}

const PRODUCTION_STEP_LABELS: readonly { type: CalculationType; label: string }[] = [
    { type: CalculationType.ACCESS_MULTI, label: 'Loading Access Multipliers…' },
    { type: CalculationType.IDEAL_MULTI, label: 'Loading Ideal Multipliers…' },
    { type: CalculationType.MODULE_GRAPH, label: 'Calculating production values…' },
] as const;

export type SiteCalculationBlockedStatusInput = {
    loadingCompleted: boolean;
    refreshingShade: boolean;
    productionValuesReady: boolean;
    shadeCalculatingIdx?: number;
    shadeCalculatingLength?: number;
    siteModelLoadPending?: boolean;
    siteViewerCalculationsPending?: boolean;
};

export type RunAiBlockedStatusInput = SiteCalculationBlockedStatusInput & {
    runAiReady: boolean;
    aiLoading: boolean;
};

type CalculationOption = {
    useCalculation?: boolean,
}

function readNeedsUpdateFlags(names: readonly CalculationType[]) {
    return names.reduce<{ [value in CalculationType]?: boolean }>((p, c) => {
        p[c] = siteCalculationState.doesNeedUpdateOrUpdating(c);
        return p;
    }, {});
}

function needsUpdateFlagsEqual(
    prev: { [value in CalculationType]?: boolean },
    next: { [value in CalculationType]?: boolean },
    names: readonly CalculationType[],
) {
    return names.every((name) => prev[name] === next[name]);
}

function calcNamesKey(names: readonly CalculationType[]) {
    return names.join('\0');
}

export function useCalculationStates(names: CalculationType[], options?: CalculationOption) {
    const namesKey = calcNamesKey(names);

    const [needsUpdate, setNeedsUpdate] = useState<{ [value in CalculationType]?: boolean }>(() =>
        readNeedsUpdateFlags(names),
    );

    useEffect(() => {
        const syncName = (name: CalculationType) => {
            const next = siteCalculationState.doesNeedUpdateOrUpdating(name);
            setNeedsUpdate((state) => (state[name] === next ? state : { ...state, [name]: next }));
        };

        const syncAll = () => {
            setNeedsUpdate((prev) => {
                const next = readNeedsUpdateFlags(names);
                return needsUpdateFlagsEqual(prev, next, names) ? prev : next;
            });
        };

        const onNeedsUpdate = (e: Event) => {
            const name = (e as CustomEvent).detail.name as CalculationType;
            syncName(name);
        };

        // 'updated' means this calculation finished — don't re-read doesNeedUpdateOrUpdating
        // while a follow-on chain step may still leave transient in-flight flags ambiguous.
        const onUpdated = (e: Event) => {
            const name = (e as CustomEvent).detail.name as CalculationType;
            setNeedsUpdate((state) => (state[name] === false ? state : { ...state, [name]: false }));
        };

        for (const name of names) {
            siteCalculationState.addNeedsUpdateListener(name, onNeedsUpdate);
            siteCalculationState.addUpdatedListener(name, onUpdated);
        }

        syncAll();

        // Self-heal if a calc finished while listeners were detached / state went stale.
        const healId = window.setInterval(() => {
            syncAll();
        }, 400);

        return () => {
            window.clearInterval(healId);
            for (const name of names) {
                siteCalculationState.removeNeedsUpdateListener(name, onNeedsUpdate);
                siteCalculationState.removeUpdatedListener(name, onUpdated);
            }
        };
    }, [namesKey]);

    if (options?.useCalculation) {
        const siteDeferred =
            useViewerStore.getState().siteViewerCalculationsPending ||
            useViewerStore.getState().siteModelLoadPending;
        if (!siteDeferred) {
            siteCalculationState.useCalculations(...names);
        }
    }

    return {
        needsUpdate,
    };
}

export function useCalculationState(name: CalculationType, options?: CalculationOption) {
    const {needsUpdate} = useCalculationStates([name], options);

    return {
        needsUpdate: needsUpdate[name],
    };
}

/** Human-readable label for the production calc step currently pending or in flight. */
export function getActiveProductionCalculationLabel(): string | null {
    for (const { type, label } of PRODUCTION_STEP_LABELS) {
        if (siteCalculationState.doesNeedUpdateOrUpdating(type)) return label;
    }
    return null;
}

function resolveSiteCalculationBlockedMessage(
    input: SiteCalculationBlockedStatusInput,
    needsUpdate: { [value in CalculationType]?: boolean },
): string | null {
    if (!input.loadingCompleted) return 'Loading site…';
    if (input.siteModelLoadPending) return 'Drawing site for production calculations…';
    if (input.refreshingShade) {
        const { shadeCalculatingIdx: idx, shadeCalculatingLength: len } = input;
        if (typeof idx === 'number' && typeof len === 'number' && len > 0) {
            return `Calculating shade (${idx} of ${len})…`;
        }
        return 'Calculating shade…';
    }
    for (const { type, label } of PRODUCTION_STEP_LABELS) {
        if (needsUpdate[type] || siteCalculationState.doesNeedUpdateOrUpdating(type)) {
            return label;
        }
    }
    if (input.siteViewerCalculationsPending && !input.productionValuesReady) {
        return 'Open oneDRAW or press Start to begin production calculations.';
    }
    if (!input.productionValuesReady) return 'Preparing production values…';
    return null;
}

/** Status while site load, shade, or production calculations are still in progress. */
export function useSiteCalculationBlockedStatus(input: SiteCalculationBlockedStatusInput): string | null {
    const { needsUpdate } = useCalculationStates(PRODUCTION_CALCULATION_TYPES);

    return useMemo(
        () => resolveSiteCalculationBlockedMessage(input, needsUpdate),
        [
            input.loadingCompleted,
            input.refreshingShade,
            input.productionValuesReady,
            input.shadeCalculatingIdx,
            input.shadeCalculatingLength,
            input.siteModelLoadPending,
            input.siteViewerCalculationsPending,
            needsUpdate,
        ],
    );
}

/**
 * Status message to show while Run AI is disabled (site load, shade, or production chain).
 * Returns null when Run AI is ready or actively running.
 */
export function useRunAiBlockedStatus(input: RunAiBlockedStatusInput): string | null {
    const siteStatus = useSiteCalculationBlockedStatus(input);

    return useMemo(() => {
        if (input.runAiReady || input.aiLoading) return null;
        return siteStatus;
    }, [input.runAiReady, input.aiLoading, siteStatus]);
}

/**
 * True once production numbers are in the store and the PV Watts calc chain is idle.
 * Listeners attach only while waiting for calcs — no polling, no perpetual subscriptions.
 */
export function useProductionCalculationReady() {
    const refreshingShade = useViewerStore((s) => s.refreshingShade);
    const loadingCompleted = useViewerStore((s) => s.loadingCompleted);
    const actualWattage = useViewerStore((s) => s.actualWattage);
    const modeCount = useViewerStore((s) => s.modeCount);

    const hasProductionNumbers = useMemo(
        () => Number(actualWattage) > 0 || Number(modeCount) > 0,
        [actualWattage, modeCount],
    );

    const awaitingCalcs = Boolean(loadingCompleted && !refreshingShade && hasProductionNumbers);

    const [calcTick, setCalcTick] = useState(0);

    const ready = useMemo(() => {
        if (!awaitingCalcs) return false;
        return isProductionCalculationReady();
    }, [awaitingCalcs, calcTick]);

    useEffect(() => {
        if (!awaitingCalcs || ready) return;

        const bump = () => setCalcTick((n) => n + 1);

        for (const name of PRODUCTION_CALCULATION_TYPES) {
            siteCalculationState.addNeedsUpdateListener(name, bump);
            siteCalculationState.addUpdatedListener(name, bump);
        }

        if (isProductionCalculationReady()) {
            bump();
        }

        return () => {
            for (const name of PRODUCTION_CALCULATION_TYPES) {
                siteCalculationState.removeNeedsUpdateListener(name, bump);
                siteCalculationState.removeUpdatedListener(name, bump);
            }
        };
    }, [awaitingCalcs, ready]);

    return ready;
}

function useProductionCalcPendingState() {
    const loadingCompleted = useViewerStore((s) => s.loadingCompleted);
    const siteViewerCalculationsPending = useViewerStore((s) => s.siteViewerCalculationsPending);
    const siteModelLoadPending = useViewerStore((s) => s.siteModelLoadPending);
    const [calcTick, setCalcTick] = useState(0);

    const calcPending = useMemo(() => {
        void calcTick;
        return isAnyProductionCalculationPending();
    }, [calcTick]);

    useEffect(() => {
        const bump = () => setCalcTick((n) => n + 1);

        for (const name of PRODUCTION_CALCULATION_TYPES) {
            siteCalculationState.addNeedsUpdateListener(name, bump);
            siteCalculationState.addUpdatedListener(name, bump);
        }
        bump();

        return () => {
            for (const name of PRODUCTION_CALCULATION_TYPES) {
                siteCalculationState.removeNeedsUpdateListener(name, bump);
                siteCalculationState.removeUpdatedListener(name, bump);
            }
        };
    }, []);

    useEffect(() => {
        // Dashboard-first: oneDRAW draw + registerCalculation are deferred — do not kick calcs.
        if (siteViewerCalculationsPending || siteModelLoadPending) return;
        if (!loadingCompleted || !calcPending) return;

        void siteCalculationState.useCalculations(...PRODUCTION_CALCULATION_TYPES);
    }, [loadingCompleted, calcPending, siteViewerCalculationsPending, siteModelLoadPending]);

    return calcPending;
}

/**
 * Loading gate for production panels (efficiency, roof table, monthly estimate).
 * Shade must finish before numbers are shown — SetModuleGraph can write placeholder
 * SA 100 / TSRF 0 / kWh while "Calculating shade" is still running (Open in OneDraw).
 * After shade, stored production is enough so stale calc flags do not keep the loader up
 * (e.g. after finalize).
 */
export function useProductionPanelLoading(): boolean {
    const refreshingShade = useViewerStore((s) => s.refreshingShade);
    const shadeCalculatingLength = useViewerStore((s) => s.shadeCalculatingLength);
    const loadingCompleted = useViewerStore((s) => s.loadingCompleted);
    const modeCount = useViewerStore((s) => s.modeCount);
    const actualWattage = useViewerStore((s) => s.actualWattage);
    const utilityWattage = useViewerStore((s) => s.utilityWattage);
    const calcPending = useProductionCalcPendingState();
    const productionValuesReady = useProductionCalculationReady();

    const hasModules = Number(modeCount) > 0;
    const hasStoredProductionValues =
        Number(actualWattage) > 0 || Number(utilityWattage) > 0;
    const shadeInProgress = refreshingShade || Number(shadeCalculatingLength) > 0;

    if (!loadingCompleted) return true;
    if (shadeInProgress) return true;

    if (!hasModules) {
        return calcPending;
    }

    if (hasStoredProductionValues) {
        return false;
    }

    return calcPending || !productionValuesReady;
}

/** Status text while shade or production values are still being calculated. */
export function useProductionPanelLoadingMessage(): string | null {
    const loadingCompleted = useViewerStore((s) => s.loadingCompleted);
    const refreshingShade = useViewerStore((s) => s.refreshingShade);
    const shadeCalculatingIdx = useViewerStore((s) => s.shadeCalculatingIdx);
    const shadeCalculatingLength = useViewerStore((s) => s.shadeCalculatingLength);
    const productionValuesReady = useProductionCalculationReady();
    const isPending = useProductionPanelLoading();

    return useSiteCalculationBlockedStatus({
        loadingCompleted,
        refreshingShade,
        productionValuesReady: productionValuesReady || !isPending,
        shadeCalculatingIdx: shadeCalculatingIdx + 1,
        shadeCalculatingLength,
    });
}

/**
 * Loader copy for the Solar tile in System & Design while the site calc chain
 * (access multipliers → ideal multipliers → shade) is in progress.
 * Does not hide RetrieveSystemDesign metrics — callers show this as a banner.
 */
export function useSolarSectionCalculationMessage(): string | null {
    const refreshingShade = useViewerStore((s) => s.refreshingShade);
    const shadeCalculatingIdx = useViewerStore((s) => s.shadeCalculatingIdx);
    const shadeCalculatingLength = useViewerStore((s) => s.shadeCalculatingLength);
    const siteViewerCalculationsPending = useViewerStore((s) => s.siteViewerCalculationsPending);
    const { needsUpdate } = useCalculationStates([
        CalculationType.ACCESS_MULTI,
        CalculationType.IDEAL_MULTI,
        CalculationType.SHADE,
    ]);

    return useMemo(() => {
        // Dashboard-first load defers the chain — keep finalized Solar metrics visible.
        if (siteViewerCalculationsPending && !refreshingShade) {
            return null;
        }

        const shadeMessage = () => {
            if (
                typeof shadeCalculatingIdx === 'number' &&
                typeof shadeCalculatingLength === 'number' &&
                shadeCalculatingLength > 0
            ) {
                return `Calculating shade (${shadeCalculatingIdx + 1} of ${shadeCalculatingLength})…`;
            }
            return 'Calculating shade…';
        };

        // Live flags — React snapshots alone can stay stuck after ACCESS finishes
        // (listeners may attach before register, or miss an updated event).
        const accessPending = siteCalculationState.doesNeedUpdateOrUpdating(CalculationType.ACCESS_MULTI);
        const idealPending = siteCalculationState.doesNeedUpdateOrUpdating(CalculationType.IDEAL_MULTI);
        const shadePending =
            refreshingShade || siteCalculationState.doesNeedUpdateOrUpdating(CalculationType.SHADE);

        if (accessPending) {
            return 'Loading Access Multipliers…';
        }
        if (idealPending) {
            return 'Loading Ideal Multipliers…';
        }
        if (shadePending) {
            return shadeMessage();
        }
        return null;
    }, [
        siteViewerCalculationsPending,
        refreshingShade,
        shadeCalculatingIdx,
        shadeCalculatingLength,
        needsUpdate,
    ]);
}

/**
 * Prepares shade and production calculations once before site report / .gen export.
 * Avoids calling useCalculations on every render (which can leave the popup stuck).
 */
export function useExportCalculationPrep() {
    const loadingCompleted = useViewerStore((s) => s.loadingCompleted);
    const refreshingShade = useViewerStore((s) => s.refreshingShade);
    const shadeCalculatingIdx = useViewerStore((s) => s.shadeCalculatingIdx);
    const shadeCalculatingLength = useViewerStore((s) => s.shadeCalculatingLength);

    const [prepReady, setPrepReady] = useState(false);

    const blockedMessage = useSiteCalculationBlockedStatus({
        loadingCompleted,
        refreshingShade,
        productionValuesReady: prepReady,
        shadeCalculatingIdx: shadeCalculatingIdx + 1,
        shadeCalculatingLength,
    });

    useEffect(() => {
        if (!loadingCompleted) return;

        let cancelled = false;

        const runPrep = async (): Promise<void> => {
            try {
                await runShadeAndProductionCalculationPrep();
            } catch (err) {
                logger.error(err, 'Export calculation prep failed');
            } finally {
                if (!cancelled) setPrepReady(true);
            }
        };

        setPrepReady(false);
        void runPrep();

        return () => {
            cancelled = true;
            endFinalizeModuleGraph();
        };
    }, [loadingCompleted]);

    return {
        prepReady,
        blockedMessage,
        isBlocked: !prepReady || refreshingShade,
    };
}